import { useLayoutEffect, useState, type RefObject } from 'react';

/**
 * Anchor a floating listbox menu to its trigger in VIEWPORT coordinates.
 *
 * The inspector aside is `overflow: hidden` and its body is the scroll region, so a menu positioned
 * `absolute` inside a field row is CLIPPED as soon as the field sits near the bottom of the panel —
 * the seller opens "Description colours" (the last field of an image slide) and sees no options at
 * all. Portalling the menu to <body> escapes both clips; this hook supplies the fixed coordinates,
 * flipping the menu ABOVE the trigger when there isn't room below, and keeps them anchored while the
 * panel scrolls or the window resizes.
 */
const VIEWPORT_MARGIN = 8;
const GAP = 4;
/** Cap of a menu's own height — keep in sync with `.bld-listbox__menu { max-height: 22rem }`. */
const MAX_MENU_HEIGHT = 352;
/** Never squeeze the menu below ~3 rows: better to overlap the trigger side than to show a sliver. */
const MIN_MENU_HEIGHT = 96;

export type MenuPosition = { top: number; left: number; width: number; maxHeight: number };

function compute(trigger: HTMLElement, contentHeight: number): MenuPosition {
  const r = trigger.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const below = vh - r.bottom - GAP - VIEWPORT_MARGIN;
  const above = r.top - GAP - VIEWPORT_MARGIN;
  const wanted = Math.min(contentHeight || MAX_MENU_HEIGHT, MAX_MENU_HEIGHT);
  // Open downwards by default; flip up only when the space above is genuinely the better half.
  const flip = below < wanted && above > below;
  const maxHeight = Math.max(MIN_MENU_HEIGHT, Math.min(MAX_MENU_HEIGHT, flip ? above : below));
  const height = Math.min(wanted, maxHeight);
  const top = flip ? Math.max(VIEWPORT_MARGIN, r.top - GAP - height) : r.bottom + GAP;
  const left = Math.max(VIEWPORT_MARGIN, Math.min(r.left, vw - r.width - VIEWPORT_MARGIN));
  return { top, left, width: r.width, maxHeight };
}

export function useAnchoredMenu(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  menuRef: RefObject<HTMLElement | null>,
): MenuPosition {
  const [pos, setPos] = useState<MenuPosition>({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: MAX_MENU_HEIGHT,
  });

  // Layout effect: positioned BEFORE paint, so the menu never flashes at the wrong place.
  useLayoutEffect(() => {
    if (!open) return;
    let raf = 0;
    const reposition = () => {
      const t = triggerRef.current;
      if (!t) return;
      setPos(compute(t, menuRef.current?.scrollHeight ?? 0));
    };
    const schedule = (e?: Event) => {
      // The menu's OWN scroll never moves the trigger.
      if (e && menuRef.current?.contains(e.target as Node)) return;
      raf ||= requestAnimationFrame(() => {
        raf = 0;
        reposition();
      });
    };
    reposition();
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
    };
  }, [open, triggerRef, menuRef]);

  return pos;
}
